/**
 * Smooth display engine — time-based interpolation for the streaming counter.
 *
 * Provides getCurrentAmount() that smoothly interpolates between
 * confirmed signed states without waiting for on-chain confirmation.
 */

export class SmoothDisplayEngine {
  private startAmount: bigint = 0n;
  private ratePerSecAtomic: bigint = 0n;
  private startTimeMs: number = 0;
  private paused = false;
  private pauseOffset: bigint = 0n;

  constructor() {}

  /**
   * Initialize or reset the display with a new starting point.
   */
  reset(startAmount: bigint, ratePerSecAtomic: bigint): void {
    this.startAmount = startAmount;
    this.ratePerSecAtomic = ratePerSecAtomic;
    this.startTimeMs = Date.now();
    this.paused = false;
    this.pauseOffset = 0n;
  }

  /**
   * Update the confirmed base amount (on each cosigned state received).
   * Adjusts the start time so the counter doesn't jump.
   */
  sync(confirmedAmount: bigint): void {
    this.startAmount = confirmedAmount;
    this.startTimeMs = Date.now();
    this.pauseOffset = 0n;
  }

  /**
   * Update the streaming rate (if renegotiated mid-stream).
   */
  setRate(ratePerSecAtomic: bigint): void {
    // Capture current interpolated amount as new start
    const current = this.getCurrentAmount();
    this.startAmount = current;
    this.ratePerSecAtomic = ratePerSecAtomic;
    this.startTimeMs = Date.now();
    this.pauseOffset = 0n;
  }

  /**
   * Pause interpolation (e.g. when connection drops).
   */
  pause(): void {
    if (!this.paused) {
      this.pauseOffset = this.getElapsedAmount();
      this.paused = true;
    }
  }

  /**
   * Resume interpolation from where it paused.
   */
  resume(): void {
    if (this.paused) {
      this.startAmount = this.startAmount + this.pauseOffset;
      this.startTimeMs = Date.now();
      this.pauseOffset = 0n;
      this.paused = false;
    }
  }

  /**
   * Get the current interpolated amount for display.
   * This is the value that should be rendered in the UI counter.
   */
  getCurrentAmount(): bigint {
    if (this.paused) {
      return this.startAmount + this.pauseOffset;
    }
    return this.startAmount + this.getElapsedAmount();
  }

  /**
   * Get the elapsed time since the last sync point.
   */
  getElapsedMs(): number {
    if (this.paused) return 0;
    return Date.now() - this.startTimeMs;
  }

  /**
   * Check if the engine is currently paused.
   */
  isPaused(): boolean {
    return this.paused;
  }

  private getElapsedAmount(): bigint {
    const elapsedMs = Date.now() - this.startTimeMs;
    if (elapsedMs <= 0) return 0n;
    return (this.ratePerSecAtomic * BigInt(elapsedMs)) / 1000n;
  }
}
